import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { LegalDisclaimer } from "../components/LegalDisclaimer";
import {
  formatDate,
  formatDateTime,
  formatMoney,
  formatPercentAnnual,
  formatRateHundredths,
} from "../lib/format";
import {
  fetchClaimCalcPrint,
  fetchObligationCalcPrint,
  type CalcPrintForm,
  type PrintParty,
  type PrintSection,
} from "../lib/legal";

/**
 * Печатная форма расчёта неустойки / процентов по ст. 395 ГК РФ (E9a, ТЗ 4.2.5, В10).
 *
 * Отдельная страница без навигации приложения: юрист открывает её, печатает браузером и
 * прикладывает лист к претензии или к исковому заявлению (Р-29 — серверной генерации PDF нет,
 * «Сохранить в PDF» делает сам диалог печати).
 *
 * Здесь НИЧЕГО не считается и не сверяется: весь документ — включая расхождение со снимком суммы
 * претензии (Р-30) — приходит собранным с сервера (`legal/printForm.ts`), тем же расчётным
 * механизмом, что и карточка (Р-25). Задача компонента — разметка и печатные стили.
 *
 * Две точки входа: `/print/calc/obligation/:oblId?on=` (из карточки расчёта, по умолчанию «на
 * сегодня» по поясу организации) и `/print/calc/claim/:claimId` (из претензии — дата отсечки
 * берётся из самой претензии).
 */

const btnPrimary =
  "rounded-[var(--radius-m)] bg-accent px-3 py-1.5 text-[13px] font-bold text-surface";
const btnSecondary =
  "rounded-[var(--radius-m)] border border-border px-3 py-1.5 text-[13px] font-bold text-ink hover:bg-bg";
const cellCls = "border border-border px-2 py-1 align-top";
const headCls = "border border-border px-2 py-1 text-left text-[12px] font-bold";
const dtCls = "text-[12px] text-muted";

export function CalcPrintPage() {
  const { oblId, claimId } = useParams<{ oblId?: string; claimId?: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const on = params.get("on") ?? "";

  const [form, setForm] = useState<CalcPrintForm | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res =
      claimId !== undefined
        ? await fetchClaimCalcPrint(claimId)
        : oblId !== undefined
          ? await fetchObligationCalcPrint(oblId, on === "" ? undefined : on)
          : null;
    if (res === null) {
      setError("Не указано, что печатать.");
      return;
    }
    if (res.ok) {
      setForm(res.value);
      setError(null);
      return;
    }
    setError(res.message);
  }, [claimId, oblId, on]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Заголовок вкладки на печатных маршрутах — ИМЯ ДОКУМЕНТА, а не бренд продукта (решение
   * владельца 04.08.2026, уточнение к D-10). Причина утилитарная: браузер печатает
   * `document.title` в служебном колонтитуле листа, а колонтитулы нам нужны — на них держится
   * нумерация страниц. Лист прикладывается к претензии и к исковому заявлению, поэтому в
   * колонтитуле должно стоять «Расчёт неустойки — договор № …».
   *
   * Штампы состояния (например, «ОТМЕНЕНА» у отменённой претензии) в имя документа не идут:
   * они на самом листе, а имя файла/колонтитул от них не меняется.
   *
   * Возврат к брендингу происходит сам: при уходе с печатной страницы снова монтируется шапка
   * приложения, а она заголовок и ставит (см. lib/branding.ts).
   */
  useEffect(() => {
    if (form !== null) document.title = `${form.title} — договор № ${form.contract.number}`;
  }, [form]);

  if (error !== null) {
    return (
      <main className="mx-auto max-w-[210mm] p-6">
        <p role="alert" className="text-[14px] text-overdue-fg">
          {error}
        </p>
        <button type="button" className={`${btnSecondary} mt-3`} onClick={() => navigate(-1)}>
          Вернуться
        </button>
      </main>
    );
  }
  if (form === null) {
    return (
      <main className="mx-auto max-w-[210mm] p-6">
        <p role="status" className="text-[14px] text-muted">
          Подготовка печатной формы…
        </p>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-[210mm] bg-surface p-6 print:p-0">
      {/* Панель действий — на печать не идёт (правило: печатается документ, а не интерфейс). */}
      <div className="no-print mb-4 flex flex-wrap gap-2">
        <button type="button" className={btnPrimary} onClick={() => window.print()}>
          Печать
        </button>
        <button type="button" className={btnSecondary} onClick={() => navigate(-1)}>
          Вернуться
        </button>
      </div>

      {form.orgMissing.length > 0 && (
        <p role="alert" className="no-print mb-4 border border-soon-fg p-3 text-[14px]">
          В реквизитах организации не заполнено: {form.orgMissing.join(", ")}. Форма распечатается,
          но шапка будет неполной — обратитесь к администратору системы.
        </p>
      )}

      <article className="text-[13px] leading-snug">
        <OrgHeader form={form} />

        <h1 className="doc-title mt-4 text-center text-[18px] font-bold text-ink">{form.title}</h1>
        <p className="mt-1 text-center text-[13px]">
          по состоянию на {formatDate(form.on)}
          {form.claim !== null &&
            ` · к претензии № ${form.claim.outNumber} от ${formatDate(form.claim.sentDate)}`}
        </p>

        {/* Штамп отмены — через всю форму, текстом и рамкой (не только цветом, docs/07 §2):
            отменённая претензия печатается, но перепутать её с действующей нельзя. */}
        {form.cancellation !== null && (
          <p className="avoid-break mt-3 border-2 border-overdue-fg p-2 text-center text-overdue-fg">
            <span className="block text-[16px] font-bold tracking-[0.3em]">ОТМЕНЕНА</span>
            <span className="text-[12px]">
              Претензия отменена
              {form.cancellation.date !== null && ` ${formatDate(form.cancellation.date)}`}.
              Причина: {form.cancellation.reason}
            </span>
          </p>
        )}

        <Section title="Стороны">
          <PartyBlock label="Кредитор (кому причитается)" party={form.creditor} />
          <PartyBlock label="Должник" party={form.debtor} />
        </Section>

        <Section title="Основание расчёта">
          <Row label="Договор">
            № {form.contract.number} от {formatDate(form.contract.date)} (
            {form.contract.directionLabel})
          </Row>
          {form.contract.subject !== null && <Row label="Предмет">{form.contract.subject}</Row>}
          {form.contract.specification !== null && (
            <Row label="Приложение к договору">{form.contract.specification}</Row>
          )}
          {form.contract.court !== null && <Row label="Подсудность">{form.contract.court}</Row>}
          <Row label="Обязательство">
            {form.obligation.typeLabel} — {form.obligation.descr}
          </Row>
          <Row label="Сумма обязательства">{formatMoney(form.obligation.amount)}</Row>
          {form.claim !== null && (
            <Row label="Претензия">
              № {form.claim.outNumber} от {formatDate(form.claim.sentDate)}; сумма требования{" "}
              {formatMoney(form.claim.amount)}; срок ответа {form.claim.answerDays} кал. дн.
            </Row>
          )}
        </Section>

        {/* Состояние и остаток долга верны на ДАТУ ФОРМИРОВАНИЯ, а расчёт выше — на дату отсечки.
            При печати к претензии эти даты расходятся, поэтому блок подписан своей датой: иначе
            «расчёт на 30.06 — 23 000 ₽» рядом с «остаток 0,00 ₽, исполнено» читалось бы как
            ошибка расчёта, хотя обе цифры верны каждая на свою дату. */}
        <Section
          title={
            form.current.sameDate
              ? "Состояние обязательства"
              : `Состояние обязательства на ${formatDate(form.current.asOf)} (дату формирования)`
          }
        >
          <Row label="Остаток основного долга">{formatMoney(form.current.remaining)}</Row>
          <Row label="Состояние">
            {form.current.statusLabel}
            {form.obligation.disputeCaseNo !== null &&
              `, судебное дело № ${form.obligation.disputeCaseNo}`}
            {form.obligation.manualStatusDate !== null &&
              ` (решение от ${formatDate(form.obligation.manualStatusDate)})`}
            {form.obligation.manualStatusReason !== null &&
              `. Основание: ${form.obligation.manualStatusReason}`}
          </Row>
          {!form.current.sameDate && (
            <p className="mt-1 text-[12px] text-muted">
              Расчёт выше построен на {formatDate(form.on)}; остаток долга и состояние приведены на
              дату формирования настоящей формы.
            </p>
          )}
        </Section>

        <Section title="Период начисления">
          <Row label="Срок исполнения по договору">
            {formatDate(form.accrual.dueComputed)}
            {form.accrual.dueShifted &&
              " (последний день срока перенесён на ближайший рабочий — ст. 193 ГК РФ)"}
          </Row>
          {form.accrual.override !== null && (
            <Row label="Начисление ведётся с даты, указанной юристом">
              {formatDate(form.accrual.override.date)}. Основание: {form.accrual.override.reason}
            </Row>
          )}
          <Row label="Начисление">
            {/* Периода может не быть вовсе (снято/исполнено до начала начисления либо просрочки
                ещё нет) — признак приходит с сервера, «перевёрнутый» диапазон не печатаем. */}
            {form.accrual.noAccrualPeriod
              ? "не велось"
              : `с ${formatDate(form.accrual.from)} по ${formatDate(form.accrual.to)}`}
            {form.accrual.stoppedByDoneDate &&
              ` (начисление прекращено датой исполнения обязательства ${formatDate(form.accrual.doneDate)})`}
            {/* Признак и дату считает сервер (D-6) — лист лишь печатает то, что и карточка. */}
            {form.accrual.stoppedByCancelled &&
              ` (начисление прекращено с ${formatDate(form.accrual.cancelledDate)} — обязательство снято с контроля${
                form.obligation.manualStatusReason === null
                  ? ""
                  : `; основание: ${form.obligation.manualStatusReason}`
              })`}
          </Row>
          <Row label="База начисления">{formatMoney(form.accrual.base)}</Row>
        </Section>

        {form.sections.map((s) => (
          <AccrualTable key={s.kind} section={s} />
        ))}

        <p className="avoid-break mt-4 border-t-2 border-ink pt-2 text-[15px] font-bold">
          {form.total.label}: <span className="num">{formatMoney(form.total.amount)}</span>
        </p>

        {form.notices.map((n) => (
          <p key={n.code} className="mt-2 text-[12px] text-muted">
            {n.message}
          </p>
        ))}

        <LegalDisclaimer />

        <div className="avoid-break mt-8 grid grid-cols-3 gap-6 text-[12px]">
          <SignatureLine label="Должность" />
          <SignatureLine label="Подпись" />
          <SignatureLine label="Расшифровка подписи" />
        </div>

        <p className="mt-6 text-[11px] text-muted">
          Форма сформирована автоматизированной системой контроля исполнения договоров{" "}
          {formatDateTime(form.printedAt)} (время Омска).
        </p>
      </article>
    </main>
  );
}

function OrgHeader({ form }: { form: CalcPrintForm }) {
  const org = form.org;
  const line = [
    org.inn === "" ? null : `ИНН ${org.inn}`,
    org.kpp === "" ? null : `КПП ${org.kpp}`,
    org.ogrn === "" ? null : `ОГРН ${org.ogrn}`,
  ]
    .filter((v) => v !== null)
    .join(" · ");
  const contacts = [org.phone, org.email].filter((v) => v !== "").join(" · ");

  return (
    <header className="avoid-break border-b border-ink pb-2 text-center">
      <p className="doc-title text-[15px] font-bold text-ink">
        {org.name === "" ? "Реквизиты организации не заполнены" : org.name}
      </p>
      {line !== "" && <p className="text-[12px]">{line}</p>}
      {org.address !== "" && <p className="text-[12px]">{org.address}</p>}
      {contacts !== "" && <p className="text-[12px]">{contacts}</p>}
      {/* Неполнота бланка видна и НА БУМАГЕ: экранное предупреждение на печать не идёт, а лист с
          отсутствующим ИНН или адресом уходит контрагенту — молчать об этом нельзя. */}
      {form.orgMissing.length > 0 && (
        <p className="text-[11px] text-overdue-fg">
          Реквизиты организации заполнены не полностью: не указано — {form.orgMissing.join(", ")}.
        </p>
      )}
    </header>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="avoid-break mt-4">
      <h2 className="text-[14px] font-bold text-ink">{title}</h2>
      <dl className="mt-1 space-y-1">{children}</dl>
    </section>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className={dtCls}>{label}:</dt>
      <dd className="text-[13px] text-ink">{children}</dd>
    </div>
  );
}

function PartyBlock({ label, party }: { label: string; party: PrintParty }) {
  const requisites = [
    party.inn === null ? null : `ИНН ${party.inn}`,
    party.kpp === null ? null : `КПП ${party.kpp}`,
    party.ogrn === null ? null : `ОГРН ${party.ogrn}`,
  ]
    .filter((v) => v !== null)
    .join(" · ");

  return (
    <div className="mt-1">
      <dt className={dtCls}>{label}:</dt>
      <dd className="text-[13px] text-ink">
        {party.name}
        {requisites !== "" && <span className="block text-[12px] text-muted">{requisites}</span>}
        {party.address !== null && (
          <span className="block text-[12px] text-muted">{party.address}</span>
        )}
      </dd>
    </div>
  );
}

/** Таблица «как посчитано» (ТЗ 4.2.5): период, дни, база, ставка, сумма. */
function AccrualTable({ section }: { section: PrintSection }) {
  return (
    <section className="mt-4" aria-labelledby={`print-${section.kind}`}>
      <h2 id={`print-${section.kind}`} className="text-[14px] font-bold text-ink">
        {section.title}
        {section.primary ? " — основной расчёт" : " — справочно"}
      </h2>

      {section.periods.length === 0 ? (
        <p className="mt-1 text-[13px]">За период начисление не производится — просрочки нет.</p>
      ) : (
        <table className="mt-2 w-full border-collapse text-[12px]">
          <caption className="sr-only">
            {section.title}: период, дни, база, ставка, сумма начисления
          </caption>
          <thead>
            <tr>
              <th scope="col" className={headCls}>
                Период
              </th>
              <th scope="col" className={headCls}>
                Дней
              </th>
              <th scope="col" className={headCls}>
                База
              </th>
              <th scope="col" className={headCls}>
                Ставка
              </th>
              <th scope="col" className={headCls}>
                Сумма
              </th>
            </tr>
          </thead>
          <tbody>
            {section.periods.map((p) => (
              <tr key={`${p.from}-${p.to}-${p.base}`} className="avoid-break">
                <td className={cellCls}>
                  {formatDate(p.from)} — {formatDate(p.to)}
                </td>
                <td className={`${cellCls} num`}>{p.days}</td>
                <td className={`${cellCls} num`}>{formatMoney(p.base)}</td>
                <td className={`${cellCls} num`}>
                  {section.kind === "penalty"
                    ? formatRateHundredths(p.rate)
                    : formatPercentAnnual(p.rate)}
                </td>
                <td className={`${cellCls} num`}>{formatMoney(p.sum)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="avoid-break">
              <td className={`${cellCls} font-bold`} colSpan={4}>
                Итого за {section.days} дн.
              </td>
              <td className={`${cellCls} num font-bold`}>{formatMoney(section.total)}</td>
            </tr>
          </tfoot>
        </table>
      )}

      {section.cap !== null && (
        <p className="avoid-break mt-2 text-[12px]">
          Начислено {formatMoney(section.cap.accrued)}, применён договорный потолок неустойки{" "}
          {formatMoney(section.cap.cap)}
          {section.cap.source !== null && ` (${section.cap.source})`} — итог ограничен потолком,
          поэтому сумма строк больше итога.
        </p>
      )}
    </section>
  );
}

/** Поле для подписи: линия и подпись под ней (заполняется от руки). */
function SignatureLine({ label }: { label: string }) {
  return (
    <div>
      <div className="h-8 border-b border-ink" />
      <p className="mt-1 text-center text-[11px] text-muted">{label}</p>
    </div>
  );
}
